import type { ChartRawData } from '../catalog/types';
import type {
  ChartAxisSeries,
  ChartValuesAndLabels,
  TripletSeriesWithLabels,
} from 'shared/types/analytics';
import { isTripletSeries } from '../utils/isTripletSeries';

export const EMPTY_CHART_RAW_DATA: ChartRawData = {
  values: [],
  labels: [],
};

export function mapValuesAndLabels(
  series: ChartValuesAndLabels | undefined
): ChartRawData {
  if (!series) {
    return EMPTY_CHART_RAW_DATA;
  }

  const [values, labels] = series;
  return {
    values: (values ?? []).map((value) => Number(value)),
    labels: labels ?? [],
  };
}

export function mapAxisSeries(
  series: ChartAxisSeries | undefined,
  categories?: readonly string[]
): ChartRawData {
  if (!series || series.length === 0) {
    return EMPTY_CHART_RAW_DATA;
  }

  const labels = categories ?? series[0]?.labels ?? [];
  const values = (series[0]?.data ?? []).map((value) => Number(value));

  return {
    values,
    labels,
  };
}

export function mapLineSeries(
  series: ChartAxisSeries | undefined,
  categories?: readonly string[]
): ChartRawData {
  if (!series || series.length === 0) {
    return EMPTY_CHART_RAW_DATA;
  }

  const labels = categories ?? series[0]?.labels ?? [];
  const stringCategories = labels.map((label) => String(label));
  const values = (series[0]?.data ?? []).map((value) => Number(value));

  return {
    values,
    labels,
    categories: stringCategories,
    lineSeries: series.map((entry, index) => ({
      label: entry.name ?? `Series ${index + 1}`,
      values: (entry.data ?? []).map((value) => Number(value)),
    })),
  };
}

export function mapTripletSeries(
  series: TripletSeriesWithLabels | undefined
): ChartRawData {
  if (!series) {
    return EMPTY_CHART_RAW_DATA;
  }

  const [triplet, labels] = series;
  if (!isTripletSeries(triplet)) {
    return EMPTY_CHART_RAW_DATA;
  }

  return {
    values: [],
    labels: [],
    tripletValues: triplet.map((entry) => entry.value),
    tripletLabels: labels,
  };
}
